import type { Contract } from '@/api/generated/models';
import type { StatusTrackerStepData } from '@/components/common';
import { ContractStatus } from '@/api/generated/models/contractStatus';
import i18n from '@/i18n';

export const statusConfig: Record<ContractStatus, { labelKey: string; bg: string; fg: string }> = {
  [ContractStatus.DRAFT]: {
    labelKey: 'contracts.status.draft',
    bg: 'var(--color-bg-surface-2)',
    fg: 'var(--color-text-secondary)'
  },
  [ContractStatus.SENT]: {
    labelKey: 'contracts.status.sent',
    bg: 'var(--color-status-warning-soft)',
    fg: 'var(--color-status-warning)'
  },
  [ContractStatus.ACCEPTED]: {
    labelKey: 'contracts.status.accepted',
    bg: 'var(--color-status-success-soft)',
    fg: 'var(--color-status-success)'
  },
  [ContractStatus.REJECTED]: {
    labelKey: 'contracts.status.rejected',
    bg: 'var(--color-status-danger-soft)',
    fg: 'var(--color-status-danger)'
  },
  [ContractStatus.ACTIVE]: {
    labelKey: 'contracts.status.active',
    bg: 'var(--color-role-user-soft)',
    fg: 'var(--color-role-user)'
  },
  [ContractStatus.COMPLETED]: {
    labelKey: 'contracts.status.completed',
    bg: 'var(--color-bg-surface-2)',
    fg: 'var(--color-text-secondary)'
  },
  [ContractStatus.CLOSED]: {
    labelKey: 'contracts.status.closed',
    bg: 'var(--color-bg-surface-2)',
    fg: 'var(--color-text-secondary)'
  },
  [ContractStatus.CANCELLED]: {
    labelKey: 'contracts.status.cancelled',
    bg: 'var(--color-status-danger-soft)',
    fg: 'var(--color-status-danger)'
  },
  [ContractStatus.ON_HOLD]: {
    labelKey: 'contracts.status.onHold',
    bg: 'var(--color-status-warning-soft)',
    fg: 'var(--color-status-warning)'
  }
};

/**
 * CON-09 (clientInitiated): nakon slanja isti SENT status čita se kao "Poslan" na klijentovoj
 * strani (koja ga je poslala) i "Zaprimljen" na partnerovoj strani (koja ga je primila) —
 * bg/fg/icon ostaju isti (statusConfig), mijenja se samo label. Normalan smjer (partner šalje
 * klijentu) zadržava jedinstven "Poslan" label na obje strane, nepromijenjeno.
 */
export const contractStatusLabelKey = (contract: Contract, viewerOrgId?: string): string => {
  const status = contract.status ?? ContractStatus.DRAFT;
  if (
    status === ContractStatus.SENT &&
    contract.clientInitiated &&
    viewerOrgId === contract.partnerOrgId
  ) {
    return 'contracts.status.received';
  }
  return statusConfig[status].labelKey;
};

const FLOW_ORDER = [
  ContractStatus.DRAFT,
  ContractStatus.SENT,
  ContractStatus.ACCEPTED,
  ContractStatus.ACTIVE,
  ContractStatus.COMPLETED,
  ContractStatus.CLOSED
];

/**
 * Koraci za StatusTracker (Figma node 4859:13767) — vidi offerDisplay.ts (buildOfferTrackerSteps)
 * za opću napomenu o partner/klijent razlici. Razlike ovdje:
 * - ON_HOLD nije zaseban korak nego "paused" na trenutnoj poziciji ACTIVE (privremena pauza
 *   rada, ne izlaz iz tijeka) — vidi Status Tracker dokumentaciju ("Paused = privremeno stanje").
 * - CANCELLED se, za razliku od REJECTED (uvijek nakon SENT), može dogoditi iz više točaka
 *   tijeka (Nacrt, Aktivan...), a model ne pamti iz koje — konzervativno se prikazuje samo
 *   Nacrt kao "done" prije iznimke, da se ne tvrdi lažni napredak koji ne možemo potvrditi.
 * - viewerPassedThroughDraft: NIJE isto što i isPartnerView — kod clientInitiated ugovora (CON-09,
 *   obrnuti smjer) klijent je taj tko stvarno posjeduje DRAFT redak (kreira ugovor iz vlastite
 *   prihvaćene ponude), partnerova zrcaljena kopija nastaje tek na send(), izravno u statusu SENT.
 *   U normalnom toku je obrnuto. Pozivatelj (ContractDetailPage) računa ovo preko
 *   isDraftAuthor, ne preko golog isPartnerView.
 */
export const buildContractTrackerSteps = (
  status: ContractStatus,
  viewerPassedThroughDraft: boolean
): StatusTrackerStepData[] => {
  const t = i18n.t.bind(i18n);
  const labelFor = (s: ContractStatus) => t(statusConfig[s].labelKey);

  if (status === ContractStatus.REJECTED) {
    const steps: StatusTrackerStepData[] = [];
    if (viewerPassedThroughDraft) {
      steps.push({ key: 'draft', label: labelFor(ContractStatus.DRAFT), state: 'done' });
    }
    steps.push({ key: 'sent', label: labelFor(ContractStatus.SENT), state: 'done' });
    steps.push({ key: 'exception', label: labelFor(ContractStatus.REJECTED), state: 'exception' });
    return steps;
  }

  if (status === ContractStatus.CANCELLED) {
    const steps: StatusTrackerStepData[] = [];
    if (viewerPassedThroughDraft) {
      steps.push({ key: 'draft', label: labelFor(ContractStatus.DRAFT), state: 'done' });
    }
    steps.push({ key: 'exception', label: labelFor(ContractStatus.CANCELLED), state: 'exception' });
    return steps;
  }

  const order = viewerPassedThroughDraft ? FLOW_ORDER : FLOW_ORDER.slice(1);
  const effectiveStatus = status === ContractStatus.ON_HOLD ? ContractStatus.ACTIVE : status;
  const currentIndex = order.indexOf(effectiveStatus);

  return order.map((s) => {
    const index = order.indexOf(s);
    let state: StatusTrackerStepData['state'];
    if (index < currentIndex) state = 'done';
    else if (index > currentIndex) state = 'upcoming';
    else state = status === ContractStatus.ON_HOLD ? 'paused' : 'current';
    return { key: s, label: labelFor(s), state };
  });
};
